import { type RecordItem } from "@softr-io/blocks-sdk";
import { useSyncExternalStore } from "react";

/**
 * Stores the rows each dynamic block has currently fetched and rendered, keyed by
 * block hrid. Collection blocks broadcast `get-records-<hrid>` once when their data
 * resolves (see the dispatch in each block's data hook) — there is no replay. The
 * AgentDialog panel reads this via `window.SoftrPageRenderer` to enrich the agent's
 * page context.
 */
let recordsByBlock = new Map<string, RecordItem[]>();
const listeners = new Set<() => void>();

function notifyAll() {
    listeners.forEach((notify) => notify());
}

/**
 * Starts listening for the `get-records-<hrid>` events of the given blocks, and returns
 * a cleanup function.
 *
 * Call this once from the app root (`PageRoot`), not from the AgentDialog panel. Blocks
 * fire the event a single time when their data first resolves; the panel loads lazily and
 * might not be mounted yet (e.g. a page that finished loading before the user opened the
 * agent) — if it started the listeners itself, those first rows would be lost. Starting
 * at the app root means we capture rows from the moment the page loads.
 */
export function setupRecordsByBlockListener(hrids: readonly string[]) {
    const handlers = hrids.map((hrid) => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<RecordItem[]>).detail;
            if (!Array.isArray(detail)) return;
            // Replace the map so `useSyncExternalStore` sees a changed snapshot reference.
            recordsByBlock = new Map(recordsByBlock).set(hrid, detail);
            notifyAll();
        };
        window.addEventListener(`get-records-${hrid}`, handler);
        return { hrid, handler };
    });

    return () => {
        handlers.forEach(({ hrid, handler }) => window.removeEventListener(`get-records-${hrid}`, handler));
        // Drop the rows we held — the page these blocks belonged to is going away.
        recordsByBlock = new Map();
        notifyAll();
    };
}

function subscribe(notify: () => void) {
    listeners.add(notify);
    return () => listeners.delete(notify);
}

function getSnapshot() {
    return recordsByBlock;
}

export function useRecordsByBlock(): Map<string, RecordItem[]> {
    return useSyncExternalStore(subscribe, getSnapshot);
}
